/* Upakarna cart events (P2.1, 19 Oct 2026): the ONE fetch interceptor for cart mutations.
   up-cart-drawer.js and upk-cart-guard.js each used to patch window.fetch on their own, so a single /cart/add ran two
   wrappers and two refresh schedules raced each other. This file installs the only wrapper (loaded first, from
   layout/theme.liquid) and tells everyone else through document events:
     upk:cart:add     after a successful /cart/add
     upk:cart:change  after a successful /cart/change, /cart/update or /cart/clear
   detail: { url }. Listeners: up-cart-drawer.js (drawer refresh, gift removal), upk-gift-autoadd.js (gift add),
   upk-cart-guard.js (bundle integrity).
   It also carries the shared ladder helpers so the add and remove scripts count the cart the same way:
     window.UpkCart.getConfig()               the parsed #UpCartConfig (re-read every call; it re-renders with the drawer)
     window.UpkCart.isGift(item)              a _gift line
     window.UpkCart.qualifying(cart, config)  the ladder total, the mirror of snippets/upk-ladder-qualifying (P6b.1,
                                              19 Oct 2026): every paid line counts, letters and charms included, at
                                              original_line_price before any code or cart discount (P6b.2; config
                                              countBeforeDiscount false: final_line_price after discounts, the P2
                                              rule); not counted are the theme's own gift
                                              lines (_gift), the handles Liquid printed to #UpCartConfig.excludeHandles
                                              (the Theme-settings list plus the gift box and the gift steps), the
                                              product ids in excludeProductIds (lines tagged launch-gift, which
                                              /cart.js cannot show) and any line priced at 0.
   P8.2 (decision 137, prompt pack P8 step 2): the same wrapper is the pixel bridge for add to cart. After every
   successful /cart/add, whoever made the call (the PDP buy box, Dawn's product form, the quick view, the rails, the
   stack and charm builders, the drawer's own rows, the wishlist, the influencer grids), it dispatches Shopify's standard
   add event on document, new StandardEvents.CartLinesUpdateEvent({ action: 'add', ... }) (event name
   shopify:cart:lines-update, the module loaded in layout/theme.liquid), with one line per added variant and a promise
   that resolves to the full cart, the shape Dawn's product-form.js dispatches for its own form. Lines carrying the
   theme's _gift property (the ladder's free gifts, added by upk-gift-autoadd.js, and the gift colour swap) are filtered
   out here, so a free gift is never reported as an add to cart; an add that consists of gift lines only dispatches
   nothing. window.UpkCart.bridgesAddEvents tells product-form.js to leave its own dispatch out, so one add is one event. */
(function () {
  'use strict';
  if (window.UpkCart) return;

  function getConfig() {
    try { return JSON.parse(document.getElementById('UpCartConfig').textContent) || {}; } catch (e) { return {}; }
  }
  function isGift(item) {
    return !!(item && item.properties && (item.properties._gift === 'true' || item.properties._gift === true));
  }
  function qualifying(cart, config) {
    config = config || getConfig();
    var excludeIds = (config.excludeProductIds || []).map(String);
    var handles = (config.excludeHandles || []).map(function (h) { return String(h).toLowerCase(); });
    /* The gift steps and the gift box are inside excludeHandles already; folded in again so a config rendered before
       P6b.1 (no excludeHandles yet) still counts the same way. */
    (config.gifts || []).forEach(function (g) { if (g && g.handle) handles.push(String(g.handle).toLowerCase()); });
    if (config.giftboxHandle) handles.push(String(config.giftboxHandle).toLowerCase());
    var before = config.countBeforeDiscount !== false;
    return ((cart && cart.items) || []).reduce(function (sum, item) {
      if (isGift(item)) return sum;
      if (item.handle && handles.indexOf(String(item.handle).toLowerCase()) !== -1) return sum;
      if (excludeIds.indexOf(String(item.product_id)) !== -1) return sum;
      if (!(item.original_line_price > 0)) return sum;
      return sum + (before ? (item.original_line_price || 0) : (item.final_line_price || 0));
    }, 0);
  }

  /* P8.2: where the add happened, for the event's context field (Dawn uses 'product' and 'cart'). */
  function pageContext() {
    var main = document.querySelector('main[data-template]');
    var t = (main && main.getAttribute('data-template')) || '';
    if (t === 'product') return 'product';
    if (t === 'cart') return 'cart';
    if (t === 'collection' || t === 'search' || t === 'list-collections') return 'collection';
    return 'page';
  }
  /* P8.2: the quantities and gift flags the caller asked for, read from the request body (JSON, FormData or a form
     string), keyed by variant id. null when the body cannot be read; the response then supplies the quantities. */
  function requestedLines(init) {
    var out = {}; var any = false;
    function take(id, quantity, gift) {
      if (!id) return;
      var k = String(id); var q = parseInt(quantity, 10) || 1;
      out[k] = { quantity: (out[k] ? out[k].quantity : 0) + q, gift: !!gift || (out[k] ? out[k].gift : false) };
      any = true;
    }
    try {
      var body = init && init.body;
      if (!body) return null;
      if (typeof body === 'string') {
        var s = body.trim();
        if (s.charAt(0) === '{') {
          var j = JSON.parse(s);
          var items = Array.isArray(j.items) ? j.items : (j.id ? [j] : []);
          items.forEach(function (it) {
            if (!it || !it.id) return;
            var p = it.properties || {};
            take(it.id, it.quantity, p._gift === 'true' || p._gift === true);
          });
        } else {
          var sp = new URLSearchParams(s);
          take(sp.get('id'), sp.get('quantity'), String(sp.get('properties[_gift]') || '') === 'true');
        }
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        take(body.get('id'), body.get('quantity'), String(body.get('properties[_gift]') || '') === 'true');
      } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        take(body.get('id'), body.get('quantity'), String(body.get('properties[_gift]') || '') === 'true');
      } else {
        return null;
      }
    } catch (e) { return null; }
    return any ? out : null;
  }
  /* P8.2: dispatch the standard add event for the paid lines of one successful /cart/add response. */
  function bridgeAdd(data, req, cartFetch) {
    var SE = window.StandardEvents; var Ev = SE && SE.CartLinesUpdateEvent;
    if (!Ev || !data) return;
    var items = Array.isArray(data.items) ? data.items : ((data.variant_id || data.id) ? [data] : []);
    var lines = [];
    items.forEach(function (it) {
      var vid = String(it.variant_id || it.id || '');
      if (!vid) return;
      var props = it.properties || {};
      var gift = props._gift === 'true' || props._gift === true || !!(req && req[vid] && req[vid].gift);
      if (gift) return; /* the theme's own free gift lines are never an add to cart */
      var q = (req && req[vid]) ? req[vid].quantity : (parseInt(it.quantity, 10) || 1);
      lines.push({ merchandiseId: vid, quantity: q });
    });
    if (!lines.length) return;
    var deferred = Ev.createPromise();
    try {
      document.dispatchEvent(new Ev({ action: 'add', context: pageContext(), lines: lines, promise: deferred.promise }));
    } catch (e) { return; }
    cartFetch(((window.routes && window.routes.cart_url) || '/cart') + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { deferred.resolve({ cart: Ev.createCartFromAjaxResponse(cart) }); })
      .catch(function (e) { deferred.reject(e); });
  }

  /* P13b (Priyal's decision 2, 20 Oct 2026): a code from Theme settings > "Codes that switch the ladder off" (VAULT70,
     printed to #UpCartConfig.blockCodes) applied to the cart means no free gift: up-cart-drawer.js removes every gift line
     and upk-gift-autoadd.js adds none. Read from the cart's own discount_codes (the Ajax API lists an applied code with
     applicable true), so it holds before the drawer re-renders. */
  function blocked(cart, config) {
    config = config || getConfig();
    var list = (config.blockCodes && config.blockCodes.length ? config.blockCodes : ['VAULT70']).map(function (c) { return String(c).toUpperCase(); });
    return ((cart && cart.discount_codes) || []).some(function (d) {
      return d && d.code && d.applicable !== false && list.indexOf(String(d.code).toUpperCase()) !== -1;
    });
  }

  window.UpkCart = { getConfig: getConfig, isGift: isGift, qualifying: qualifying, blocked: blocked, bridgesAddEvents: true };

  if (window.__upkCartFetchPatched) return;
  window.__upkCartFetchPatched = true;
  var nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function') return;
  window.fetch = function (input) {
    var url = '';
    try { url = (typeof input === 'string') ? input : (input && input.url) || ''; } catch (e) { /* noop */ }
    var init = arguments[1];
    var result = nativeFetch.apply(this, arguments);
    var m = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.exec(url);
    if (m) {
      var kind = m[1] === 'add' ? 'add' : 'change';
      var req = kind === 'add' ? requestedLines(init) : null;
      result.then(function (res) {
        if (!res || !res.ok) return;
        try { document.dispatchEvent(new CustomEvent('upk:cart:' + kind, { detail: { url: url } })); } catch (e) { /* noop */ }
        if (kind !== 'add') return;
        var clone;
        try { clone = res.clone(); } catch (e) { return; }
        clone.json().then(function (data) { bridgeAdd(data, req, nativeFetch); }, function () { /* not JSON: nothing to report */ });
      }, function () { /* noop */ });
    }
    return result;
  };
})();
